import { useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Phone, MessageCircle, ArrowLeft, ArrowUpRight, CheckCircle2 } from "lucide-react";

export const TOWNS = {
    birmingham: { name: "Birmingham", council: "Birmingham City Council" },
    nottingham: { name: "Nottingham", council: "Nottingham City Council" },
    leicester: { name: "Leicester", council: "Leicester City Council" },
    coventry: { name: "Coventry", council: "Coventry City Council" },
    derby: { name: "Derby", council: "Derby City Council" },
    wolverhampton: { name: "Wolverhampton", council: "City of Wolverhampton Council" },
    lincoln: { name: "Lincoln", council: "Lincolnshire County Council" },
};

const STEPS = ["Planning & survey", "Council permission", "Excavation", "Installation"];

export default function TownPage() {
    const { slug } = useParams();
    const navigate = useNavigate();
    const town = TOWNS[slug];

    useEffect(() => {
        document.title = town
            ? `Dropped Kerbs in ${town.name} | Drop Kerbs UK`
            : "Drop Kerbs UK | Dropped Kerbs from Planning to Installation";
        window.__lenis?.scrollTo(0, { immediate: true });
    }, [town]);

    if (!town) {
        return (
            <main className="min-h-[70vh] flex flex-col items-start justify-center px-5 sm:px-8 pt-16" data-testid="town-not-found">
                <h1 className="font-display uppercase text-5xl mb-6">Area not found.</h1>
                <Link to="/" data-testid="town-not-found-home-link" className="px-8 py-4 bg-amber text-tarmac font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-paper transition-colors duration-200">
                    Back to home
                </Link>
            </main>
        );
    }

    const others = Object.entries(TOWNS).filter(([s]) => s !== slug);

    return (
        <main className="bg-tarmac grain relative" data-testid={`town-page-${slug}`}>
            <section className="px-5 sm:px-8 pt-32 pb-24 max-w-7xl mx-auto relative z-10">
                <Link to="/" data-testid="town-back-link" className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] uppercase text-grit hover:text-amber transition-colors duration-200 mb-10">
                    <ArrowLeft size={14} /> All areas
                </Link>

                <motion.p
                    className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-4"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ duration: 0.6 }}
                >
                    [ Dropped kerb specialists — {town.name} ]
                </motion.p>

                <h1 className="mb-8">
                    {["DROPPED", "KERBS IN", `${town.name.toUpperCase()}.`].map((line, i) => (
                        <span key={line} className="block overflow-hidden pb-[0.08em] -mb-[0.08em]">
                            <motion.span
                                className={`block font-display uppercase leading-[0.85] tracking-tight text-[13vw] sm:text-[10vw] lg:text-[8vw] ${i === 2 ? "text-amber" : "text-paper"}`}
                                initial={{ y: "110%" }}
                                animate={{ y: 0 }}
                                transition={{ duration: 0.9, delay: 0.15 + i * 0.12, ease: [0.16, 1, 0.3, 1] }}
                            >
                                {line}
                            </motion.span>
                        </span>
                    ))}
                </h1>

                <motion.div
                    className="grid grid-cols-1 lg:grid-cols-2 gap-12 border-t border-ash pt-10"
                    initial={{ opacity: 0, y: 24 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.7, delay: 0.7 }}
                >
                    <div>
                        <p className="text-base lg:text-lg text-grit leading-relaxed mb-8">
                            Need smooth, legal vehicle access to your {town.name} drive or
                            garage? We handle the entire {town.council} dropped kerb
                            application — survey, drawings, fees, excavation and the final
                            tarmac finish. One company, one fixed price.
                        </p>
                        <ul className="mb-10 space-y-3">
                            {STEPS.map((s, i) => (
                                <li key={s} className="flex items-center gap-3 font-mono text-sm tracking-[0.1em] uppercase text-paper/80" data-testid={`town-step-${i}`}>
                                    <CheckCircle2 size={16} className="text-amber shrink-0" /> {s} — handled
                                </li>
                            ))}
                        </ul>
                        <div className="flex flex-wrap gap-3">
                            <button
                                data-testid="town-get-quote-btn"
                                onClick={() => navigate("/#quote")}
                                className="px-8 py-4 bg-amber text-tarmac font-mono text-xs tracking-[0.2em] uppercase font-bold hover:bg-paper transition-colors duration-200"
                            >
                                Get a free quote
                            </button>
                            <a
                                data-testid="town-call-btn"
                                href="tel:+447971124650"
                                className="flex items-center gap-2 px-8 py-4 border border-ash font-mono text-xs tracking-[0.2em] uppercase hover:border-amber hover:text-amber transition-colors duration-200"
                            >
                                <Phone size={14} /> 07971 124 650
                            </a>
                            <a
                                data-testid="town-whatsapp-btn"
                                href={`https://wa.me/447971124650?text=Hi%20Drop%20Kerbs%20UK%2C%20I%27d%20like%20a%20quote%20for%20a%20dropped%20kerb%20in%20${town.name}.`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="flex items-center gap-2 px-8 py-4 border border-ash font-mono text-xs tracking-[0.2em] uppercase hover:border-amber hover:text-amber transition-colors duration-200"
                            >
                                <MessageCircle size={14} /> WhatsApp
                            </a>
                        </div>
                    </div>

                    <div className="border border-ash p-8 sm:p-10 self-start">
                        <p className="font-mono text-xs tracking-[0.25em] uppercase text-amber mb-6">Also covering</p>
                        <div className="flex flex-col">
                            {others.map(([s, t]) => (
                                <Link
                                    key={s}
                                    to={`/areas/${s}`}
                                    data-testid={`town-other-${s}-link`}
                                    className="group flex items-center justify-between border-t border-ash last:border-b py-4 font-mono text-sm tracking-[0.15em] uppercase text-grit hover:text-amber transition-colors duration-200"
                                >
                                    Dropped kerbs in {t.name}
                                    <ArrowUpRight size={16} className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-200" />
                                </Link>
                            ))}
                        </div>
                    </div>
                </motion.div>
            </section>
        </main>
    );
}
